import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Plus, X, Pencil, Trash2, CheckCircle, XCircle,
  CreditCard, Percent, Package, List, ChevronRight, ChevronLeft,
  ToggleLeft, ToggleRight, Users, FolderOpen, Eye,
} from 'lucide-react'
import { plansService } from '@/services/plansService'
import { categoriesService } from '@/services/categoriesService'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { getLocalizedField } from '@/utils/formatters'

const EMPTY_FORM = {
  nameAr: '', nameEn: '',
  descAr: '', descEn: '',
  defaultCommissionRate: '',
  maxProducts: '',
  categoryIds: [],
  isActive: true,
}

const STEPS = [
  { id: 1, label: 'معلومات الخطة', icon: CreditCard },
  { id: 2, label: 'العمولة والحدود', icon: Percent },
  { id: 3, label: 'الفئات المسموحة', icon: FolderOpen },
]

function DialogOverlay({ children, onClose, wide }) {
  return (
    <div
      onClick={onClose}
      style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="animate-in"
        style={{ background:'#fff', borderRadius:24, padding:0, width:'100%', maxWidth: wide ? 640 : 480, boxShadow:'0 32px 64px rgba(0,0,0,0.2)', position:'relative', maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column' }}
      >
        <button
          onClick={onClose}
          style={{ position:'absolute', top:16, left:16, width:32, height:32, borderRadius:10, background:'#F3F3F3', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#767683', zIndex:1 }}
        >
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#454652', marginBottom:5, fontFamily:'Cairo, sans-serif' }}>
        {label}{required && <span style={{ color:'#BA1A1A', marginRight:3 }}>*</span>}
      </label>
      {children}
    </div>
  )
}

function StepIndicator({ currentStep }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:0, padding:'20px 28px 0', direction:'rtl' }}>
      {STEPS.map((step, i) => {
        const done   = currentStep > step.id
        const active = currentStep === step.id
        const Icon   = step.icon
        return (
          <div key={step.id} style={{ display:'flex', alignItems:'center', flex: i < STEPS.length - 1 ? 1 : 0 }}>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
              <div style={{
                width:36, height:36, borderRadius:12,
                background: done ? '#1B6D24' : active ? '#000666' : '#F3F3F3',
                color: done || active ? '#fff' : '#767683',
                display:'flex', alignItems:'center', justifyContent:'center',
                transition:'all 0.2s',
              }}>
                {done ? <CheckCircle size={17} /> : <Icon size={17} />}
              </div>
              <span style={{ fontSize:10, fontWeight:700, fontFamily:'Cairo, sans-serif', color: active ? '#000666' : done ? '#1B6D24' : '#767683', whiteSpace:'nowrap' }}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex:1, height:2, background: done ? '#1B6D24' : '#F3F3F3', margin:'0 6px', marginBottom:20, transition:'background 0.3s' }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

function CategorySelector({ selected, onChange }) {
  const { data: tree } = useQuery({
    queryKey: ['categories-tree'],
    queryFn: categoriesService.getTree,
  })

  const flatCats = []
  const flatten = (arr, depth = 0) => {
    arr?.forEach((c) => {
      flatCats.push({ id: c.id, name: c.name?.ar || c.name?.en || '', depth })
      if (c.children?.length) flatten(c.children, depth + 1)
    })
  }
  flatten(tree)

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]
    onChange(next)
  }

  return (
    <div style={{ maxHeight:300, overflowY:'auto', display:'flex', flexDirection:'column', gap:4 }}>
      {flatCats.length === 0 ? (
        <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683', textAlign:'center', padding:20 }}>
          لا توجد فئات بعد
        </p>
      ) : (
        flatCats.map((cat) => (
          <label key={cat.id}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderRadius:10, cursor:'pointer', background: selected.includes(cat.id) ? 'rgba(0,6,102,0.06)' : 'transparent', transition:'background 0.12s' }}>
            <input type="checkbox" checked={selected.includes(cat.id)} onChange={() => toggle(cat.id)}
              style={{ width:18, height:18, accentColor:'#000666', cursor:'pointer', flexShrink:0 }} />
            <span style={{ fontFamily:'Cairo, sans-serif', fontSize:13, fontWeight: selected.includes(cat.id) ? 700 : 500, color:'#1A1C1C', marginRight: cat.depth * 16 }}>
              {cat.depth > 0 && '— '.repeat(cat.depth)}{cat.name}
            </span>
          </label>
        ))
      )}
    </div>
  )
}

function PlanFormDialog({ mode, initialData, onClose }) {
  const qc = useQueryClient()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState(initialData || EMPTY_FORM)

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }))

  const createMut = useMutation({
    mutationFn: () => plansService.create({
      name:                  { ar: form.nameAr, en: form.nameEn || form.nameAr },
      description:           { ar: form.descAr, en: form.descEn },
      defaultCommissionRate: form.defaultCommissionRate ? parseFloat(form.defaultCommissionRate) : undefined,
      maxProducts:           form.maxProducts            ? parseInt(form.maxProducts)            : null,
      categoryIds:           form.categoryIds,
      isActive:              form.isActive,
    }),
    onSuccess: () => {
      toast.success('تم إنشاء الخطة بنجاح')
      qc.invalidateQueries({ queryKey: ['plans'] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل إنشاء الخطة'),
  })

  const updateMut = useMutation({
    mutationFn: () => plansService.update(initialData.id, {
      name:                  { ar: form.nameAr, en: form.nameEn || form.nameAr },
      description:           { ar: form.descAr, en: form.descEn },
      defaultCommissionRate: form.defaultCommissionRate !== '' ? parseFloat(form.defaultCommissionRate) : null,
      maxProducts:           form.maxProducts            ? parseInt(form.maxProducts)            : null,
      categoryIds:           form.categoryIds,
      isActive:              form.isActive,
    }),
    onSuccess: () => {
      toast.success('تم تحديث الخطة')
      qc.invalidateQueries({ queryKey: ['plans'] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل تحديث الخطة'),
  })

  const isCreate = mode === 'create'
  const isPending = createMut.isPending || updateMut.isPending

  const step1Valid = form.nameAr.trim()

  const handleNext = () => setStep((s) => s + 1)
  const handleBack = () => setStep((s) => s - 1)

  const handleSubmit = () => {
    if (isCreate) createMut.mutate()
    else updateMut.mutate()
  }

  return (
    <DialogOverlay onClose={onClose} wide>
      <StepIndicator currentStep={step} />

      <div style={{ padding:'20px 28px', overflowY:'auto', flex:1 }}>
        <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:17, color:'#000666', marginBottom:4 }}>
          {isCreate ? 'إنشاء خطة جديدة' : 'تعديل الخطة'}
        </h3>
        <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683', marginBottom:20 }}>
          {step === 1 && 'أدخل المعلومات الأساسية للخطة'}
          {step === 2 && 'حدد نسبة العمولة والحد الأقصى للمنتجات'}
          {step === 3 && 'اختر الفئات التي يُسمح للبائعين في هذه الخطة بالبيع فيها'}
        </p>

        {step === 1 && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <Field label="اسم الخطة (عربي)" required>
              <input className="hg-input" placeholder="مثال: الخطة الاحترافية" value={form.nameAr} onChange={set('nameAr')} />
            </Field>
            <Field label="Plan Name (English)">
              <input className="hg-input" placeholder="e.g. Professional Plan" value={form.nameEn} onChange={set('nameEn')} dir="ltr" />
            </Field>
            <div style={{ gridColumn:'1 / -1' }}>
              <Field label="وصف الخطة (عربي)">
                <textarea className="hg-input" rows={3} placeholder="وصف مختصر للخطة..." value={form.descAr} onChange={set('descAr')} style={{ resize:'vertical' }} />
              </Field>
            </div>
            <div style={{ gridColumn:'1 / -1' }}>
              <Field label="Plan Description (English)">
                <textarea className="hg-input" rows={3} placeholder="Brief plan description..." value={form.descEn} onChange={set('descEn')} dir="ltr" style={{ resize:'vertical' }} />
              </Field>
            </div>
            <div style={{ gridColumn:'1 / -1', display:'flex', alignItems:'center', gap:12, padding:'12px 0' }}>
              <button type="button" onClick={() => setForm((p) => ({ ...p, isActive: !p.isActive }))}
                style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center' }}>
                {form.isActive
                  ? <ToggleRight size={28} style={{ color:'#1B6D24' }} />
                  : <ToggleLeft  size={28} style={{ color:'#C4C4C4' }} />}
              </button>
              <span style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, color:'#454652' }}>
                الخطة {form.isActive ? 'مفعّلة' : 'معطّلة'}
              </span>
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <Field label="نسبة العمولة الافتراضية (%)">
              <div style={{ position:'relative' }}>
                <input className="hg-input" type="number" min="0" max="100" step="0.5"
                  placeholder="مثال: 8.5" value={form.defaultCommissionRate} onChange={set('defaultCommissionRate')} dir="ltr" style={{ paddingLeft:36 }} />
                <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#767683', fontWeight:700, fontSize:14 }}>%</span>
              </div>
              <p style={{ fontSize:11, color:'#767683', marginTop:4, fontFamily:'Tajawal, sans-serif' }}>
                تُنسخ تلقائياً إلى البائع عند تسجيله بهذه الخطة. يمكن تعديلها لكل بائع لاحقاً.
              </p>
            </Field>
            <Field label="الحد الأقصى للمنتجات">
              <input className="hg-input" type="number" min="1" step="1"
                placeholder="اتركه فارغاً = غير محدود" value={form.maxProducts} onChange={set('maxProducts')} dir="ltr" />
            </Field>
            <div style={{ gridColumn:'1 / -1', borderRadius:14, background:'rgba(0,6,102,0.04)', border:'1px solid rgba(0,6,102,0.08)', padding:16 }}>
              <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, color:'#000666', marginBottom:10 }}>ملخص الخطة</div>
              <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                <SummaryItem label="العمولة الافتراضية" value={form.defaultCommissionRate ? `${form.defaultCommissionRate}%` : '—'} icon={Percent} />
                <SummaryItem label="المنتجات" value={form.maxProducts || 'غير محدود'} icon={Package} />
                <SummaryItem label="الفئات" value={`${form.categoryIds.length} فئة`} icon={FolderOpen} />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683', marginBottom:12 }}>
              اختر الفئات التي يُسمح للبائعين في هذه الخطة بالبيع فيها. اترك الكل بدون تحديد للسماح بجميع الفئات.
            </p>
            <CategorySelector
              selected={form.categoryIds}
              onChange={(ids) => setForm((p) => ({ ...p, categoryIds: ids }))}
            />
          </div>
        )}
      </div>

      <div style={{ padding:'16px 28px', borderTop:'1px solid #F3F3F3', display:'flex', gap:10 }}>
        {step > 1 && (
          <button type="button" onClick={handleBack}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 18px', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, background:'#F3F3F3', color:'#454652', border:'none', cursor:'pointer' }}>
            <ChevronRight size={15} />
            السابق
          </button>
        )}
        <div style={{ flex:1 }} />
        {step < STEPS.length ? (
          <button type="button" onClick={handleNext}
            disabled={step === 1 && !step1Valid}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, background:'#000666', color:'#fff', border:'none', cursor: !step1Valid && step === 1 ? 'not-allowed' : 'pointer', opacity: step === 1 && !step1Valid ? 0.5 : 1, boxShadow:'0 4px 16px rgba(0,6,102,0.2)' }}>
            التالي
            <ChevronLeft size={15} />
          </button>
        ) : (
          <button type="button" onClick={handleSubmit} disabled={isPending}
            style={{ padding:'10px 24px', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, background:'#1B6D24', color:'#fff', border:'none', cursor:'pointer', opacity: isPending ? 0.6 : 1, boxShadow:'0 4px 16px rgba(27,109,36,0.25)' }}>
            {isPending ? 'جارٍ الحفظ...' : isCreate ? 'إنشاء الخطة' : 'حفظ التعديلات'}
          </button>
        )}
      </div>
    </DialogOverlay>
  )
}

function SummaryItem({ label, value, icon: Icon }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ width:30, height:30, borderRadius:8, background:'rgba(0,6,102,0.08)', color:'#000666', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <Icon size={14} />
      </div>
      <div>
        <div style={{ fontSize:10, color:'#767683', fontFamily:'Tajawal, sans-serif' }}>{label}</div>
        <div style={{ fontSize:13, fontWeight:700, color:'#1A1C1C', fontFamily:'Cairo, sans-serif' }}>{value}</div>
      </div>
    </div>
  )
}

function PlanCard({ plan, onEdit, onDelete, onViewVendors }) {
  const name = plan.name?.ar || plan.name?.en || '—'
  const catCount = plan.categories?.length || plan.categoryIds?.length || 0

  return (
    <div
      style={{ background:'#fff', borderRadius:20, padding:22, boxShadow:'0 2px 4px rgba(26,28,28,0.04),0 8px 16px rgba(26,28,28,0.06)', transition:'transform 0.2s,box-shadow 0.2s', position:'relative', overflow:'hidden' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(26,28,28,0.12)' }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(26,28,28,0.04),0 8px 16px rgba(26,28,28,0.06)' }}
    >
      <div style={{ position:'absolute', top:16, left:16 }}>
        <span style={{
          fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:8,
          fontFamily:'Cairo, sans-serif',
          background: plan.isActive ? 'rgba(27,109,36,0.1)' : 'rgba(186,26,26,0.08)',
          color: plan.isActive ? '#1B6D24' : '#BA1A1A',
        }}>
          {plan.isActive ? 'مفعّل' : 'معطّل'}
        </span>
      </div>

      <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:16, paddingTop:4 }}>
        <div style={{ width:48, height:48, borderRadius:14, background:'linear-gradient(135deg,#000666,#1A237E)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <CreditCard size={22} style={{ color:'#fff' }} />
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:16, color:'#1A1C1C', marginBottom:3 }}>{name}</div>
          {plan.name?.en && plan.name?.ar && (
            <div style={{ fontFamily:'Tajawal, sans-serif', fontSize:12, color:'#767683' }}>{plan.name.en}</div>
          )}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:16 }}>
        <MetricPill icon={Percent}    label="العمولة"    value={plan.defaultCommissionRate != null ? `${plan.defaultCommissionRate}%` : '—'} />
        <MetricPill icon={Package}    label="المنتجات"   value={plan.maxProducts || '∞'} />
        <MetricPill icon={Users}      label="البائعون"   value={plan._count?.vendors ?? plan.vendorCount ?? '—'} />
      </div>

      {catCount > 0 && (
        <div style={{ marginBottom:16, paddingBottom:16, borderBottom:'1px solid #F3F3F3' }}>
          <div style={{ fontSize:11, color:'#767683', fontFamily:'Tajawal, sans-serif', marginBottom:6 }}>الفئات المسموحة ({catCount})</div>
          <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
            {(plan.categories || []).slice(0, 5).map((cat) => (
              <span key={cat.id} style={{ fontSize:10, padding:'2px 8px', borderRadius:6, background:'rgba(0,6,102,0.06)', color:'#000666', fontFamily:'Tajawal, sans-serif' }}>
                {getLocalizedField(cat.name)}
              </span>
            ))}
            {catCount > 5 && (
              <span style={{ fontSize:10, color:'#767683', fontFamily:'Tajawal, sans-serif' }}>+{catCount - 5}</span>
            )}
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:8 }}>
        <button onClick={onViewVendors}
          style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'8px 0', borderRadius:10, fontSize:12, fontWeight:700, fontFamily:'Cairo, sans-serif', border:'none', cursor:'pointer', transition:'all 0.15s', background:'rgba(0,6,102,0.06)', color:'#000666' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,6,102,0.12)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,6,102,0.06)'}>
          <Eye size={14} />عرض البائعين
        </button>
        <button onClick={onEdit}
          style={{ width:36, height:36, borderRadius:10, background:'rgba(0,6,102,0.07)', color:'#000666', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,6,102,0.14)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,6,102,0.07)'}>
          <Pencil size={14} />
        </button>
        <button onClick={onDelete}
          style={{ width:36, height:36, borderRadius:10, background:'rgba(186,26,26,0.07)', color:'#BA1A1A', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(186,26,26,0.14)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(186,26,26,0.07)'}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  )
}

function MetricPill({ icon: Icon, label, value }) {
  return (
    <div style={{ borderRadius:10, background:'rgba(0,6,102,0.04)', padding:'8px 10px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:3 }}>
        <Icon size={11} style={{ color:'#767683' }} />
        <span style={{ fontSize:10, color:'#767683', fontFamily:'Tajawal, sans-serif' }}>{label}</span>
      </div>
      <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:14, color:'#1A1C1C' }}>{value}</div>
    </div>
  )
}

function VendorsDialog({ plan, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['plan-vendors', plan.id],
    queryFn: () => plansService.getVendors(plan.id),
  })

  const vendors = data?.vendors || data || []

  return (
    <DialogOverlay onClose={onClose}>
      <div style={{ padding:28 }}>
        <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:17, color:'#000666', marginBottom:4 }}>
          البائعون في "{plan.name?.ar || plan.name?.en || ''}"
        </h3>
        <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683', marginBottom:20 }}>
          {vendors.length} بائع{' '}
          {plan.defaultCommissionRate != null && `· عمولة ${plan.defaultCommissionRate}%`}
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
        ) : vendors.length === 0 ? (
          <EmptyState icon={Users} message="لا يوجد بائعون على هذه الخطة" />
        ) : (
          <div className="space-y-2">
            {vendors.map((v) => (
              <div key={v.id}
                style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:12, border:'1px solid #F3F3F3', background:'#FAFAFA' }}>
                <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#000666,#1A237E)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:13, fontFamily:'Cairo, sans-serif', flexShrink:0 }}>
                  {(v.storeName?.ar || v.storeName?.en || 'V').charAt(0)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, color:'#1A1C1C' }}>
                    {getLocalizedField(v.storeName)}
                  </div>
                  <div style={{ fontFamily:'Tajawal, sans-serif', fontSize:11, color:'#767683' }}>
                    @{v.storeSlug || '—'} · عمولة {v.commissionRate ?? plan.defaultCommissionRate}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DialogOverlay>
  )
}

export default function PlansPage() {
  const qc = useQueryClient()

  const [formDialog,     setFormDialog]     = useState({ open:false, mode:'create', plan:null })
  const [deleteDialog,   setDeleteDialog]   = useState({ open:false, plan:null })
  const [vendorsDialog,  setVendorsDialog]  = useState({ open:false, plan:null })

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['plans'],
    queryFn: () => plansService.getAll(),
  })

  const plans = data?.plans || []

  const deleteMut = useMutation({
    mutationFn: (id) => plansService.delete(id),
    onSuccess: () => {
      toast.success('تم حذف الخطة')
      qc.invalidateQueries({ queryKey: ['plans'] })
      setDeleteDialog({ open:false, plan:null })
    },
    onError: () => toast.error('فشل الحذف — قد يكون هناك بائعون على هذه الخطة'),
  })

  const openEdit = (plan) => {
    setFormDialog({
      open: true,
      mode: 'edit',
      plan: {
        id:                    plan.id,
        nameAr:                plan.name?.ar || '',
        nameEn:                plan.name?.en || '',
        descAr:                plan.description?.ar || '',
        descEn:                plan.description?.en || '',
        defaultCommissionRate: plan.defaultCommissionRate ?? '',
        maxProducts:           plan.maxProducts ?? '',
        categoryIds:           plan.categoryIds || plan.categories?.map((c) => c.id) || [],
        isActive:              plan.isActive ?? true,
      },
    })
  }

  return (
    <div className="space-y-5">

      <div className="hg-card p-4 flex items-center justify-between gap-3">
        <div>
          <h2 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:20, color:'#000666', marginBottom:2 }}>خطط البائعين</h2>
          <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683' }}>
            أنشئ وأدر خطط الاشتراك التي يختار منها البائعون عند التسجيل
          </p>
        </div>
        <button onClick={() => setFormDialog({ open:true, mode:'create', plan:null })}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white flex-shrink-0"
          style={{ background:'#000666', fontFamily:'Cairo, sans-serif', boxShadow:'0 4px 16px rgba(0,6,102,0.25)' }}>
          <Plus size={16} />
          خطة جديدة
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
      ) : isError ? (
        <div className="hg-card p-8 flex flex-col items-center gap-4">
          <p style={{ color:'#BA1A1A', fontWeight:700, fontFamily:'Cairo, sans-serif' }}>فشل تحميل الخطط</p>
          <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">إعادة المحاولة</button>
        </div>
      ) : plans.length === 0 ? (
        <div className="hg-card">
          <EmptyState icon={List} message="لا توجد خطط بعد — أنشئ خطتك الأولى" />
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onEdit={() => openEdit(plan)}
              onDelete={() => setDeleteDialog({ open:true, plan })}
              onViewVendors={() => setVendorsDialog({ open:true, plan })}
            />
          ))}
        </div>
      )}

      {formDialog.open && (
        <PlanFormDialog
          mode={formDialog.mode}
          initialData={formDialog.plan}
          onClose={() => setFormDialog({ open:false, mode:'create', plan:null })}
        />
      )}

      {vendorsDialog.open && (
        <VendorsDialog
          plan={vendorsDialog.plan}
          onClose={() => setVendorsDialog({ open:false, plan:null })}
        />
      )}

      <ConfirmDialog
        open={deleteDialog.open}
        title="حذف الخطة"
        message={`هل تريد حذف خطة "${deleteDialog.plan?.name?.ar || ''}"؟ لا يمكن التراجع.`}
        variant="danger"
        loading={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate(deleteDialog.plan.id)}
        onCancel={() => setDeleteDialog({ open:false, plan:null })}
      />
    </div>
  )
}
